import SEOHead from './SEOHead';

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Briefcase, MapPin, Clock, Search, Building2, 
  IndianRupee, Bookmark, BookmarkCheck 
} from 'lucide-react';
import { toast } from 'sonner';
import { buildJobUrl } from '../utils/urls';
import Footer from './Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const JobBoard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    fetchJobs();
    fetchSavedJobs();
  }, []);

  const fetchJobs = async (search = '', location = '') => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/api/jobs?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (location) url += `location=${encodeURIComponent(location)}&`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setJobs(data.jobs);
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/my/saved`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSavedJobs(data.jobs.map(j => j.id));
      }
    } catch (error) {
      // Ignore
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs(searchQuery, locationFilter);
  };

  const handleSave = async (e, jobId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/${jobId}/save`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        if (data.saved) {
          setSavedJobs(prev => [...prev, jobId]);
        } else {
          setSavedJobs(prev => prev.filter(id => id !== jobId));
        }
        toast.success(data.message);
      }
    } catch (error) {
      toast.error('Failed to save job');
    }
  };

  const formatSalary = (min, max, currency) => {
    if (!min && !max) return null;
    const format = (num) => {
      if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
      if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
      return num.toLocaleString();
    };
    if (min && max) return `${format(min)} - ${format(max)}`;
    if (min) return `From ${format(min)}`;
    return `Up to ${format(max)}`;
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week(s) ago`;
    return `${Math.floor(diffDays / 30)} month(s) ago`;
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'full-time': return 'bg-green-100 text-green-700 border-green-200';
      case 'internship': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'contract': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'part-time': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const jobTypes = [
    { id: 'all', name: 'All Jobs' },
    { id: 'full-time', name: 'Full-time' },
    { id: 'part-time', name: 'Part-time' },
    { id: 'contract', name: 'Contract' },
    { id: 'internship', name: 'Internship' }
  ];

  const filteredJobs = selectedType === 'all' 
    ? jobs 
    : jobs.filter(job => job.job_type === selectedType);

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <SEOHead
        title="Student Jobs & Internships"
        description="Browse the latest student jobs, internships and graduate opportunities posted by verified employers. Apply directly through Nerd Nation."
        path="/job-board"
      />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Briefcase className="h-8 w-8 text-orange-500" />
            Job Board
          </h1>
          <p className="text-gray-600 mt-2">Find your dream job or internship opportunity</p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search by job title, skills, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="relative sm:w-48">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Location"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
              Search
            </Button>
          </div>
        </form>

        {/* Type Filter Pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {jobTypes.map(type => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedType === type.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-orange-300 hover:text-orange-600'
              }`}
            >
              {type.name}
            </button>
          ))}
        </div>

        {/* Job Listings */}
        {loading ? (
          <div className="text-center py-12">Loading jobs...</div>
        ) : filteredJobs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No jobs found</h3>
              <p className="text-gray-500 mt-1">Try adjusting your search or check back later</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map(job => (
              <Card 
                key={job.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(buildJobUrl(job))}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        {job.company_logo ? (
                          <img 
                            src={job.company_logo.startsWith('data:') ? job.company_logo : `${API}${job.company_logo}`} 
                            alt={job.company_name} 
                            className="w-12 h-12 object-cover rounded-lg flex-shrink-0" loading="lazy" decoding="async"/>
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="h-6 w-6 text-orange-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="text-lg font-semibold text-gray-900 hover:text-orange-600 transition-colors">
                              {job.title}
                            </h3>
                            <button 
                              onClick={(e) => handleSave(e, job.id)}
                              className="p-1 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                            >
                              {savedJobs.includes(job.id) ? (
                                <BookmarkCheck className="h-5 w-5 text-orange-500" />
                              ) : (
                                <Bookmark className="h-5 w-5 text-gray-400" />
                              )}
                            </button>
                          </div>
                          <p className="text-gray-600 font-medium">{job.company_name}</p>
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {job.location}
                            </span>
                            {formatSalary(job.salary_min, job.salary_max, job.salary_currency) && (
                              <span className="flex items-center gap-1">
                                <IndianRupee className="h-4 w-4" />
                                {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {getTimeAgo(job.created_at)}
                            </span>
                          </div>
                          {job.tags && job.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {job.tags.slice(0, 4).map((tag, idx) => (
                                <Badge key={idx} variant="secondary" className="bg-gray-100 text-gray-600 text-xs">
                                  {tag}
                                </Badge>
                              ))}
                              {job.tags.length > 4 && (
                                <Badge variant="secondary" className="bg-gray-100 text-gray-600 text-xs">
                                  +{job.tags.length - 4}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex sm:flex-col items-center sm:items-end gap-3">
                      <Badge className={`${getTypeColor(job.job_type)} capitalize`}>
                        {job.job_type.replace('-', ' ')}
                      </Badge>
                      <Button 
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(buildJobUrl(job));
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default JobBoard;
